<template>
  <div class="sm:c-col-4 md:c-col-8 lg:c-col-6 p-cadastro-rapido-container">
    <p-form
      ref="form"
      @submit="onFormSubmit"
      v-slot="{ invalid }"
    >
      <!-- Título -->
      <div class="c-row">
        <div class="c-col">
          <h3 class="c-text-h3 ui-mb-6">
            Parece que você é novo no Estratégia
          </h3>
        </div>
      </div>

      <!-- Subtítulo -->
      <div class="c-row ui-mb-4">
        <div class="c-col">
          <div class="c-text-b1 c-text-gray-400">
            Vamos criar uma conta. Para isso, informe os seguintes dados:
          </div>
        </div>
      </div>

      <!-- Email -->
      <div class="c-row ui-mb-4">
        <validation-provider
          class="c-col"
          rules="required|email"
          name="Email"
          vid="email"
          v-slot="{ errors }"
        >
          <p-input
            v-model.trim="email"
            data-cy="emailField"
            label="Email"
            type="email"
            :error-message="errors[0]"
          />
        </validation-provider>
      </div>

      <!-- Nome completo -->
      <div class="c-row ui-mb-4">
        <validation-provider
          class="c-col"
          rules="required|full_name"
          name="Nome completo"
          v-slot="{ errors }"
        >
          <p-input
            data-cy="nameField"
            key="nameField"
            v-model.trim="name"
            label="Nome completo"
            type="text"
            :error-message="errors[0]"
            :mask="fullNameRegex"
          />
        </validation-provider>
      </div>

      <!-- Telefone -->
      <div class="c-row ui-mb-4">
        <validation-provider
          class="c-col"
          rules="required|length:11"
          :custom-messages="{ length: 'Celular inválido' }"
          name="Celular"
          v-slot="{ errors }"
        >
          <p-input
            v-model="phone"
            data-cy="phoneField"
            key="phoneField"
            label="Celular*"
            name="phone"
            mask="(00) 0 0000-0000"
            hint="DDD + nº do celular"
            icon-info="info"
            :error-message="errors[0]"
          />
        </validation-provider>
      </div>

      <!-- Senha -->
      <div class="c-row ui-mb-4">
        <validation-provider
          class="c-col"
          rules="required|min:8"
          name="Senha"
          vid="password"
          v-slot="{ errors }"
          :custom-messages="{
            required: 'Senha é obrigatória',
            min: 'A senha é muito curta'
          }"
        >
          <input-password
            v-model.trim="password"
            data-cy="passwordField"
            label="Senha"
            name="passwordField"
            :error-message="errors[0]"
          />
        </validation-provider>
      </div>

      <div
        v-if="errors.length > 0"
        class="ui-my-4 text-negative"
      >
        <q-list>
          <div
            v-for="(error, index) in errors"
            :key="index"
          >
            * {{ error }}
          </div>
        </q-list>
      </div>

      <!-- Checkbox Política de Privacidade -->
      <div class="c-row ui-mb-4">
        <div class="c-col">
          <div class="c-text-b2 c-text-gray">
            <c-checkbox
              v-model="isPrivacyPolicyAccepted"
              data-testid="privacy-policy"
            >
              Li, entendi e concordo com a
              <a
                @click.stop
                :href="politicaDePrivacidadeHref"
                target="_blank"
              >
                <b><u>Política de privacidade</u></b>
              </a>
            </c-checkbox>
          </div>
        </div>
      </div>

      <!-- Botão Prosseguir -->
      <div class="c-row ui-mb-6">
        <div class="c-col">
          <c-button
            data-cy="submitButton"
            size="lg"
            type="submit"
            class="ui-w-full"
            :disabled="invalid || !isPrivacyPolicyAccepted"
            :loading="isFormSending"
          >
            Prosseguir com criação de conta
          </c-button>
        </div>
      </div>

      <!-- Social Login -->
      <div
        v-if="showSocialLogin"
        class="c-row ui-mb-4"
      >
        <div class="c-col">
          <separator />
        </div>
        <div class="c-d-flex">
          <social-login-button
            provider="facebook"
            @logged-in="handleLoggedIn"
          />
          <social-login-button
            provider="google"
            @logged-in="handleLoggedIn"
          />
          <social-login-button
            provider="apple"
            @logged-in="handleLoggedIn"
          />
        </div>
        <div class="c-col">
          <separator />
        </div>
      </div>
    </p-form>

    <!-- Divisor "Ou" -->
    <div class="login-divider ui-my-6">
      <div class="login-divider__line" />
      <span class="login-divider__text c-text-b2">Ou</span>
      <div class="login-divider__line" />
    </div>

    <!-- Já é aluno? -->
    <div class="c-row ui-mt-8">
      <div class="c-col">
        <div class="ui-flex ui-flex-col c-text-gray">
          <span class="c-text-b1 c-text-gray-500 ui-mb-2"><strong>Já é aluno?</strong></span>
          <template v-if="$slots.login">
            <slot name="login" />
          </template>
          <router-link
            v-else
            :to="{ name: 'login', query: { ...$route.query } }"
            data-cy="loginLink"
            class="width-fit"
          >
            <c-link class="padding-remover">
              Faça login com outro email
            </c-link>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CButton, CCheckbox, CLink } from '@estrategiahq/coruja-web-ui';
import { politicaDePrivacidadeHref } from '@/consts';
import PForm from '@/components/p-form';
import PInput from '@/components/p-input';
import Separator from '@/components/separator';
import SocialLoginButton from '@/components/p-social-login-button';
import { ValidationProvider } from 'vee-validate';
import { userAlreadyExistsError, rateLimitExceededError, invalidStudentNameError, invalidFullNameContainsEmojisError, invalidFullNameContainsSymbolsError, invalidFullNameLeadingOrTrailingSpacesError, invalidFullNameNoSpacesError, invalidFullNameTooShortError } from '@/utils/errors';
import InputPassword from '@/components/input-password';
import { FULL_NAME_REGEX } from '@/utils/regex';

export default {
  name: 'PCadastroRapido',
  props: {
    preventNavigation: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    CButton,
    CCheckbox,
    CLink,
    InputPassword,
    PForm,
    PInput,
    Separator,
    SocialLoginButton,
    ValidationProvider,
  },
  data: () => ({
    email: '',
    name: '',
    phone: '',
    password: '',
    isPrivacyPolicyAccepted: false,
    isFormSending: false,
    showSocialLogin: false,
    errors: [],
    politicaDePrivacidadeHref,
  }),
  mounted() {
    this.email = this.$route.query.email?.trim() ?? '';
    this.$socialLogin.someActive().then((someActive) => {
      this.showSocialLogin = someActive;
    });
  },
  computed: {
    fullNameRegex() {
      return FULL_NAME_REGEX;
    },
  },
  methods: {
    async onFormSubmit() {
      this.isFormSending = true;
      try {
        await this.$services.accounts.fastRegistration(this.email, this.name, this.phone, this.password);
        if (this.preventNavigation) {
          this.$emit('registered', { email: this.email });
        } else {
          this.$router.push({ name: 'confirme-seu-codigo', query: { ...this.$route.query, email: this.email } });
        }
      } catch (error) {
        this.errors = error.response?.data?.errors || [];
        this.showErrorToast(error);
        this.setFormErrors(error);
      } finally {
        this.isFormSending = false;
      }
    },
    showErrorToast(error) {
      if (error === rateLimitExceededError) {
        this.$toast.show({ type: 'error', text: 'Muitas tentativas. Aguarde alguns minutos e tente novamente.' });
        return;
      }
      this.$toast.show({ type: 'error', text: 'Erro ao realizar o cadastro' });
    },
    setFormErrors(error) {
      if (error === userAlreadyExistsError) {
        this.$refs.form.setErrors({ email: ['Email já cadastrado'] });
      } else if (
        error === invalidStudentNameError ||
        error === invalidFullNameContainsEmojisError ||
        error === invalidFullNameContainsSymbolsError ||
        error === invalidFullNameLeadingOrTrailingSpacesError ||
        error === invalidFullNameNoSpacesError ||
        error === invalidFullNameTooShortError
      ) {
        this.$refs.form.setErrors({ 'Nome completo': [error.message] });
      }
    },
    handleLoggedIn(val) {
      this.$emit('logged-in', val);
    },
  },
};
</script>

<style scoped>
.p-cadastro-rapido-container {
  padding: 2rem !important;
}

.login-divider {
  display: flex;
  align-items: center;
  gap: 16px;
}

.login-divider__line {
  flex: 1;
  height: 1px;
  background: #ccc;
  opacity: 0.48;
}

.login-divider__text {
  color: #9e9e9e;
  white-space: nowrap;
}

.padding-remover {
  padding: 0 !important;
  border: none !important;
}

.width-fit {
  width: fit-content !important;
}

.text-negative {
  color: var(--c-color-red-500);
}
</style>
